<template>
  <b-container
    fluid
    class="dashboard-header"
  >
    <b-row cols="12">
      <b-col xs="12">
        <b-row>
          <b-col
            v-if="shouldShowTitle"
            class="dashboard-header__column-title"
            lg="8"
            md="8"
            sm="12"
          >
            <h1>
              {{ title }}
            </h1>
            <h3>
              <auto-refresh-countdown-label
                ref="autoRefreshCountdown"
                :show-timer="false"
                @on-interval="$event.wait(refreshData())"
              />
            </h3>
          </b-col>
          <b-col
            lg="4"
            md="4"
            sm="12"
          >
            <div class="dashboard-header__column-icon-filter">
              <s-sidebar
                ref="sidebar"
                @on-show="onShowHandler"
                @on-cancel="onCancelHandler"
              >
                <template #body>
                  <reports-filter
                    v-if="isSidebarVisible()"
                    is-company-field-required
                    is-installation-field-required
                    display-fields-in-line
                    initialize-filter
                    :has-asset-field="false"
                    :filter-objects.sync="reportsFilterObjects"
                    @user-companies-have-regional="setUserCompaniesHaveRegional"
                  />
                </template>
                <template #footer>
                  <b-button
                    variant="primary"
                    class="mt-3 mt-md-0"
                    @click="onSearchButtonClick"
                  >
                    {{ $t('general.search') }}
                  </b-button>
                </template>
              </s-sidebar>
            </div>
          </b-col>
        </b-row>
        <b-row
          class="dashboard-header__row-dashboard-data"
        >
          <div
            class="dashboard-header__column-dashboard-data-header"
          >
            <default-data-header
              :items="items"
              :modal-title="modalTitle"
            />
          </div>
        </b-row>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import DefaultDataHeader from '@/components/header/DefaultDataHeader';
import DashboardHeaderMixin from '@/components/header/mixin/dashboardHeaderMixin';
import DefaultDataHeaderMixin from '@/components/header/mixin/defaultDataHeaderMixin';
import ReportsFilter from '@/components/ReportsFilter';
import AutoRefreshCountdownLabel from '@/components/AutoRefreshCountdownLabel';
import CompanyService from '@/domain/services/company';
import RegionalService from '@/domain/services/regional';
import InstallationService from '@/domain/services/installation';

export default {
  components: {
    DefaultDataHeader,
    ReportsFilter,
    AutoRefreshCountdownLabel,
  },
  mixins: [
    DefaultDataHeaderMixin,
    DashboardHeaderMixin,
  ],
  data: () => ({
    reportsFilterObjects: {},
    reportsFilterObjectsBackup: {},
    userCompaniesHaveRegional: true,
  }),
  async created() {
    await this.loadAllFilters();
    this.onAfterLoadAllFilters();
    this.refreshData();
  },
  methods: {
    setUserCompaniesHaveRegional(userCompaniesHaveRegional) {
      this.userCompaniesHaveRegional = userCompaniesHaveRegional;
    },
    resetCounting() {
      this.$refs.autoRefreshCountdown.resetCounting();
    },
    onAfterLoadAllFilters() {
      this.$emit('afterLoadAllFilters', this.getFilters());
    },
    refreshData() {
      this.$emit('refreshData', this.getFilters());
    },
    onSearchButtonClick() {
      const invalidFields = this.getInvalidFields();

      if (invalidFields.length) {
        this.$pushAlert({
          code: 'MA001',
          messageParams: {
            NomeDoCampo: invalidFields.join(', '),
          },
        });
        return;
      }
      this.refreshData();
      this.$nextTick(() => {
        this.$refs.sidebar.showSidebar(false);
      });
    },
    getFilters() {
      return {
        companiesNames: this.reportsFilterObjects?.companies?.map(x => x.name),
        companiesIds: this.reportsFilterObjects?.companies?.map(x => x.id),
        installationsNames: this.reportsFilterObjects?.installations?.map(x => x.name),
        installationsIds: this.reportsFilterObjects?.installations?.map(x => x.id),
        regionalsNames: this.reportsFilterObjects?.regional?.map(x => x.name),
        regionalsIds: this.reportsFilterObjects?.regional?.map(x => x.id),
      };
    },
    getInvalidFields() {
      const invalidFields = [];

      if (!(this.reportsFilterObjects.companies || []).length) {
        invalidFields.push(this.$t('entities.company'));
      }

      if (!(this.reportsFilterObjects.installations || []).length) {
        invalidFields.push(this.$t('entities.installation'));
      }

      return invalidFields;
    },
    saveFiltersBackup() {
      this.reportsFilterObjectsBackup = { ...this.reportsFilterObjects };
    },
    restoreFiltersBackup() {
      this.reportsFilterObjects = { ...this.reportsFilterObjectsBackup };
    },
    async loadAllFilters() {
      await this.loadCompanies();
      await this.loadRegionals();
      await this.loadInstallations();
      this.saveFiltersBackup();
    },
    async loadCompanies() {
      await CompanyService.getCompanies().then((response) => {
        this.reportsFilterObjects.companies = response || [];
      });
    },
    async loadRegionals() {
      if (!this.reportsFilterObjects.companies) {
        return;
      }
      const companiesIds = this.reportsFilterObjects.companies.map(x => x.id);
      await RegionalService.getRegional(companiesIds).then((response) => {
        this.reportsFilterObjects.regional = response || [];
      });
    },
    async loadInstallations() {
      if (!this.reportsFilterObjects.companies || !this.reportsFilterObjects.regional) {
        return;
      }
      const params = {
        companiesIds: this.reportsFilterObjects.companies.map(x => x.id),
        regionalIds: this.reportsFilterObjects.regional.map(x => x.id),
      };
      await InstallationService.getInstallationsByCompaniesOrRegional(params).then((response) => {
        this.reportsFilterObjects.installations = response || [];
      });
    },
    onShowHandler() {
      this.saveFiltersBackup();
    },
    onCancelHandler() {
      this.restoreFiltersBackup();
    },
    isSidebarVisible() {
      return this.$refs.sidebar?.$data.show ?? false;
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"
$image-size: 80px

.dashboard-header
  padding: 0

  &__row-dashboard-data
    margin-top: 1.2rem
    display: flex
    justify-content: space-between

  &__column-title
    display: flex
    align-items: baseline
    flex-wrap: wrap

    & h1
      font-size: 1.45rem
      +media-max-sm()
        margin-bottom: 1rem

    & h3
      margin-left: 1rem

  &__column-dashboard-data-header
    display: flex

  &__column-icon-filter
    display: flex
    justify-content: flex-end
</style>
